import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const documentResponseSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  content: z.string(),
  status: z.string(),
  docType: z.string().nullable(),
});

export const documentsContract = c.router(
  {
    generateDocument: {
      method: 'POST',
      path: '/:tenantId/generate',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      body: z
        .object({
          patientId: z.string().uuid(),
          appointmentId: z.string().uuid().optional(),
          prompt: z.string().max(10000).optional(),
          templateId: z.string().uuid().optional(),
        })
        .refine((data) => Boolean(data.prompt) !== Boolean(data.templateId), {
          message: 'Provide either prompt or templateId, not both or neither',
        }),
      responses: {
        200: documentResponseSchema,
        400: z.object({ error: z.string() }),
      },
    },
    generateTemplate: {
      method: 'POST',
      path: '/:tenantId/generate-template',
      pathParams: z.object({
        tenantId: z.string().uuid(),
      }),
      body: z.object({
        userUploadId: z.string().uuid(),
      }),
      responses: {
        200: z.discriminatedUnion('status', [
          z.object({
            status: z.literal('success'),
            name: z.string(),
            content: z.string(),
            instructions: z.string(),
            docType: z.string().optional(),
          }),
          z.object({
            status: z.literal('rejected'),
            reason: z.string(),
          }),
        ]),
        400: z.object({ error: z.string() }),
      },
    },
    generatePdfFormTemplateFields: {
      method: 'POST',
      path: '/:tenantId/pdf-form-template/generate',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({
        userUploadId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          generatedFileId: z.string().uuid(),
          fields: z.array(
            z.object({
              name: z.string(),
              type: z.enum(['text', 'checkbox', 'radio', 'dropdown']),
              label: z.string().optional(),
            }),
          ),
          fieldCount: z.number(),
          alreadyFillable: z.boolean(),
          coverage: z.number().nullable(),
        }),
        400: z.object({ error: z.string() }),
        422: z.object({ error: z.string() }),
      },
    },
    createPdfFormTemplate: {
      method: 'POST',
      path: '/:tenantId/pdf-form-template',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z
        .object({
          sourceFileId: z.string().uuid().optional(),
          // Back-compat alias: pre-split web bundles POST the upload id as
          // `userUploadId`. Accepted so old clients keep working through a
          // rolling deploy; the server resolves it to sourceFileId.
          userUploadId: z.string().uuid().optional(),
          name: z.string().min(1),
        })
        .refine((b) => b.sourceFileId != null || b.userUploadId != null, {
          message: 'sourceFileId is required',
        }),
      responses: {
        200: z.object({
          templateId: z.string().uuid(),
          name: z.string(),
          fieldCount: z.number(),
        }),
        400: z.object({ error: z.string() }),
        422: z.object({ error: z.string() }),
      },
    },
    fillPdfFormFromTemplate: {
      method: 'POST',
      path: '/:tenantId/fill-pdf-form-from-template',
      pathParams: z.object({ tenantId: z.string().uuid() }),
      body: z.object({
        templateId: z.string().uuid(),
        patientId: z.string().uuid(),
        appointmentId: z.string().uuid(),
      }),
      responses: {
        200: z.object({
          documentId: z.string(),
          fileName: z.string(),
          skippedFields: z.array(z.string()),
          alteredFields: z.array(z.string()),
          usedAppearanceFallback: z.boolean(),
          excludedHumanOnlyFields: z.array(z.string()),
        }),
        400: z.object({ error: z.string() }),
        404: z.object({ error: z.string() }),
      },
    },
    getFilledDocumentUrl: {
      method: 'GET',
      path: '/:tenantId/filled-document-url/:documentId',
      pathParams: z.object({
        tenantId: z.string().uuid(),
        documentId: z.string().uuid(),
      }),
      responses: {
        200: z.object({ presignedUrl: z.string().nullable() }),
      },
    },
  },
  { pathPrefix: '/documents' },
);
